<template>
  <div
    :class="[
      theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
      'rounded-lg border p-4 shadow-sm'
    ]"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 :class="[theme.isDark.value ? 'text-gray-200' : 'text-gray-900', 'text-lg font-medium']">
        Filters
      </h3>
      <button
        :class="[
          theme.isDark.value
            ? 'text-gray-400 hover:text-gray-200'
            : 'text-gray-500 hover:text-gray-700',
          'text-sm font-medium'
        ]"
        @click="clearFilters"
      >
        Clear All
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Repository Filter -->
      <div>
        <label
          for="repository"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          Repository
        </label>
        <select
          id="repository"
          v-model="localFilters.repository"
          :disabled="loadingRepositories"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500',
            loadingRepositories ? 'opacity-50 cursor-wait' : ''
          ]"
        >
          <option value="">All Repositories</option>
          <option
            v-for="repo in repositories"
            :key="repo"
            :value="repo"
          >
            {{ repo }}
          </option>
        </select>
      </div>

      <!-- Status Filter -->
      <div>
        <fieldset>
          <legend
            :class="[
              theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
              'block text-sm font-medium mb-2'
            ]"
          >
            Status
          </legend>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="status in statusOptions"
              :key="status.value"
              :class="[
                localFilters.status.includes(status.value)
                  ? status.activeClass
                  : theme.isDark.value
                    ? 'bg-gray-700 text-gray-300 border-gray-600'
                    : 'bg-gray-100 text-gray-600 border-gray-300',
                'flex items-center px-3 py-1.5 rounded-full text-sm cursor-pointer border transition-colors'
              ]"
            >
              <input
                v-model="localFilters.status"
                type="checkbox"
                :value="status.value"
                class="sr-only"
              />
              <span class="mr-1">{{ status.icon }}</span>
              <span>{{ status.label }}</span>
            </label>
          </div>
        </fieldset>
      </div>

      <!-- Gate Filter -->
      <div>
        <label
          for="gate"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          Quality Gate
        </label>
        <select
          id="gate"
          v-model="localFilters.gate"
          :disabled="loadingGates"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500',
            loadingGates ? 'opacity-50 cursor-wait' : ''
          ]"
        >
          <option value="">All Gates</option>
          <option
            v-for="gate in gates"
            :key="gate"
            :value="gate"
          >
            {{ formatGateName(gate) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Apply Filters Button -->
    <div class="mt-4 flex justify-end">
      <button
        :disabled="!hasChanges"
        :class="[
          hasChanges
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : theme.isDark.value
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          'px-4 py-2 rounded-md text-sm font-medium transition-colors'
        ]"
        @click="applyFilters"
      >
        Apply Filters
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, onMounted } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import { qualityGatesService } from '../../../services/quality-gates'
import type { QualityGatesFilters, PRStatus } from '../../../types/quality-gates'

const theme = inject('theme', useTheme())

interface Props {
  filters: QualityGatesFilters
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:filters': [filters: QualityGatesFilters]
  'apply-filters': [filters: QualityGatesFilters]
}>()

// Local filters state
const localFilters = ref<QualityGatesFilters>({ ...props.filters })

// Dropdown options
const repositories = ref<string[]>([])
const gates = ref<string[]>([])
const loadingRepositories = ref(false)
const loadingGates = ref(false)

// Status options with styling
const statusOptions: { value: PRStatus; label: string; icon: string; activeClass: string }[] = [
  {
    value: 'pass',
    label: 'Pass',
    icon: '✓',
    activeClass:
      'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-500'
  },
  {
    value: 'fail',
    label: 'Fail',
    icon: '✗',
    activeClass: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-500'
  },
  {
    value: 'warning',
    label: 'Warning',
    icon: '⚠',
    activeClass:
      'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-500'
  }
]

// Check if there are changes
const hasChanges = computed(() => {
  return JSON.stringify(localFilters.value) !== JSON.stringify(props.filters)
})

// Watch for external filter changes
watch(
  () => props.filters,
  (newFilters) => {
    localFilters.value = { ...newFilters }
  },
  { deep: true }
)

// Format gate name for display
const formatGateName = (gateName: string): string => {
  return qualityGatesService.formatGateName(gateName)
}

// Fetch dropdown options
const loadDropdownOptions = async () => {
  // Fetch repositories and gates in parallel
  loadingRepositories.value = true
  loadingGates.value = true

  try {
    const [reposResult, gatesResult] = await Promise.all([
      qualityGatesService.fetchRepositories(),
      qualityGatesService.fetchGates()
    ])
    repositories.value = reposResult
    gates.value = gatesResult
  } finally {
    loadingRepositories.value = false
    loadingGates.value = false
  }
}

// Methods
const applyFilters = () => {
  emit('update:filters', { ...localFilters.value })
  emit('apply-filters', { ...localFilters.value })
}

const clearFilters = () => {
  localFilters.value = {
    repository: '',
    status: [],
    gate: ''
  }
  applyFilters()
}

// Load dropdown options on mount
onMounted(() => {
  loadDropdownOptions()
})
</script>
